import type { PriceAndQuantityOptionInterface } from '../model/PriceAndQuantityOptionInterface';
import type { ListGroupOption } from '../../ListGroupSelectable/types';
import type { TranslationPairs } from '../model/TranslationInterface';
import { SizeHelper } from '../helper/SizeHelper';
import { AutoPickAttrProcessor } from '../context/processor/AutoPickAttrProcessor';
import { SizeProcessor } from '../context/processor/SizeProcessor';
import { ProductProcessorsEnum } from '../product/processor/ProductProcessorsEnum';
import { ProductContext } from '../product/context/ProductContext';
import type { PriceAndQuantityOption } from '../types';
import TranslationService from '../product/service/TranslationService';

export class ProductWizardContext extends ProductContext {
  public attributesRequiredByWizard: string[] = ['size', 'imperial_units', 'shape'];
  public neverDisable: string[] = ['material', 'quantity'];
  public quantityAndPriceOptions: PriceAndQuantityOptionInterface[] = [];
  public sizeHelper: SizeHelper;
  public isBusy: boolean = false;

  constructor() {
    super();

    this.sizeHelper = new SizeHelper(this);
    // Wizard Processors
    const autoPickProcess: AutoPickAttrProcessor = new AutoPickAttrProcessor(
      ['laminate'],
      this.cherryPicks,
      (attributeName) => {
        return this.neverDisable.indexOf(attributeName) >= 0;
      },
      (attributeName) => this.isAttributeRequired(attributeName),
    );
    this.processors.registerProcessor(ProductProcessorsEnum.AUTOPICK, autoPickProcess);
    this.processors.registerProcessor(
      ProductProcessorsEnum.SIZE,
      new SizeProcessor(this.sizeHelper),
    );

    // This means that "feature" and "ink" can't make "laminate" options constrained
    // For instance, when we have made a sticker with backprint in the editor
    // we still want to be able to choose from all the available laminates
    // TODO: Pass this as a argument and use it for the customer, ignore it for admin advanced mode.
    // TODO: Or instead of passing as argument, pass it as a part of the ProductAttrInterface from the backend
    this.attrEvaluator.ignoreConstraintsFromTo('feature', 'laminate');
    this.attrEvaluator.ignoreConstraintsFromTo('ink', 'laminate');
  }

  public resetQuantity(quantityAndPriceOptions: Array<PriceAndQuantityOption>): void {
    this.quantityAndPriceOptions = quantityAndPriceOptions;
    const defaultOption = this.quantityAndPriceOptions.find((option) => option.quantity !== 0);
    const defaultQuantity = defaultOption ? defaultOption.quantity : 0;

    if (!this.cherryPicks.hasAttr('quantity') && defaultQuantity) {
      this.productItem.setAttrValue('quantity', defaultQuantity);
    }
  }

  public canAttrValuesBeDisabled(attributeName: string): boolean {
    return this.neverDisable.indexOf(attributeName) < 0;
  }

  /**
   * A attribute can be required by the product service, to determine if a product is valid.
   * And there is some attribute we require by the Sticker Wizard.
   *
   * @param attributeName
   */
  public isAttributeRequired(attributeName: string): boolean {
    return (
      this.attrComputer.isRequired(attributeName) ||
      this.attributesRequiredByWizard.indexOf(attributeName) >= 0
    );
  }

  // TODO: MOVE THIS TO IT'S OWN HELPER CLASS. OptionsHelper
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  public getListOptionsForAttribute(
    attributeName: string,
    translations: TranslationPairs | undefined,
    sort: boolean = true,
    disableOutOfStockValues: boolean = true,
  ): ListGroupOption[] {
    const currentValue = this.productItem.getAttrValue<string>(attributeName);
    const optionValues = this.attrComputer.getSuggestedValues<string>(attributeName);

    if (this.attrComputer.isDynamicValue(attributeName) && currentValue != null) {
      let valueToSearch: string | number = currentValue;

      if (this.attrComputer.isNumberType(attributeName)) {
        valueToSearch = Number(currentValue);
      }

      if (!optionValues.includes(valueToSearch.toString())) {
        optionValues.push(valueToSearch.toString());
      }
    }

    const disabledValues = this.getDisabledOptions(attributeName);

    if (!disableOutOfStockValues) {
      delete disabledValues.outOfStock;
    }

    let optionTitles: TranslationPairs | null = null;

    if (translations) {
      optionTitles = TranslationService.getTranslationsFromMap(
        translations,
        'products',
        optionValues,
        attributeName,
      );
    }

    const options: ListGroupOption[] = this.mapOptions(
      optionValues,
      attributeName,
      disabledValues,
      optionTitles,
    );

    return sort ? this.sortOptions(options, attributeName) : options;
  }

  private mapOptions(
    optionValues: Array<string>,
    attributeName: string,
    disabledValues: Record<string, (string | number | boolean)[]>,
    optionTitles?: TranslationPairs | null,
  ) {
    const flattenedDisabledValues = Object.values(disabledValues).flat();
    const optionIcons = this.attrComputer.getIcons(attributeName);
    return optionValues.map((value: string) => {
      const attrValue = this.productItem.getAttrValue(attributeName);
      const isSelected = Array.isArray(attrValue)
        ? (attrValue as Array<string | number | boolean>).includes(value)
        : String(this.productItem.getAttrValue(attributeName)) === String(value);

      return {
        value: value,
        title: optionTitles?.[value] ?? value,
        badge: undefined,
        disabled: flattenedDisabledValues.includes(String(value)),
        image: {
          url: optionIcons?.[value] ?? null,
          alt: optionTitles?.[value] ?? value,
        },
        icon: optionIcons?.[value] ?? null,
        selected: isSelected,
      };
    });
  }

  private sortOptions(options: ListGroupOption[], attributeName: string): ListGroupOption[] {
    const sorted = options.reduce<{
      high: ListGroupOption[];
      medium: ListGroupOption[];
      low: ListGroupOption[];
    }>(
      (acc, option) => {
        if (!option.disabled) {
          if (
            this.attrComputer.hasBeenFiltered(attributeName) &&
            !this.attrComputer.isInFilteredValues(attributeName, option.value)
          ) {
            // Unrecommended value
            return { ...acc, medium: [...acc.medium, option] };
          } else {
            return { ...acc, high: [...acc.high, option] };
          }
        } else {
          return { ...acc, low: [...acc.low, option] };
        }
      },
      { high: [], medium: [], low: [] },
    );

    return [...sorted.high, ...sorted.medium, ...sorted.low];
  }

  public getDisabledOptions(attributeName: string): Record<string, (string | number | boolean)[]> {
    const constrainedValues = this.canAttrValuesBeDisabled(attributeName)
      ? this.attrComputer.getConstrainedValues(attributeName)
      : [];
    const outOfStockValues = this.attrComputer.getOutOfStockValues(attributeName) ?? [];
    const disabledOptions = {
      constrained: constrainedValues,
      outOfStock: outOfStockValues,
    };

    return disabledOptions;
  }
}
